import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Home, Users, ChevronLeft, ChevronRight, Crown, Building2, MapPinned, Church, Droplets } from "lucide-react";
import SearchableSelect from "../components/SearchableSelect";

const LABEL_OPSI = [{ value: "non_kristen", label: "Ada anggota Non-Kristen" }, { value: "belum_baptis", label: "Ada anggota Belum Baptis" }];
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Breadcrumb } from "../components/Breadcrumb";

export default function KeluargaListPage() {
  const navigate = useNavigate();
  const canWrite = useAuth().can("keluarga:write");
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [label, setLabel] = useState("");
  const [labels, setLabels] = useState({});
  useEffect(() => { api.get("/keluarga/non-jemaat/label").then((r) => setLabels(r.data.per_keluarga || {})).catch(() => {}); }, [data]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 10 };
      if (search) params.search = search;
      if (label) params.label = label;
      const r = await api.get("/keluarga", { params });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [page, search, label]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);
  const [sealamat, setSealamat] = useState({});
  useEffect(() => { api.get("/keluarga-sealamat/ringkasan").then((r) => setSealamat(r.data.per_keluarga || {})).catch(() => {}); }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Breadcrumb items={[{ label: "Data Keluarga" }, { label: "Kartu Keluarga" }]} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Kartu Keluarga</h1>
          <p className="text-sm text-slate-500 mt-1">
            Total <span className="font-semibold text-slate-700" data-testid="total-keluarga">{data.total}</span> keluarga terdaftar
          </p>
        </div>
        {canWrite && <Button onClick={() => navigate("/keluarga/tambah")} data-testid="add-keluarga-btn" className="min-h-[44px] rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]">
          <Plus className="h-4 w-4 mr-2" /> Tambah Kartu Keluarga
        </Button>}
      </div>

      <Card className="p-4 rounded-2xl border-slate-200/80 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="search-keluarga-input" placeholder="Cari nama keluarga, No KK, alamat..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10 min-h-[44px] rounded-xl" />
          </div>
          <div className="sm:w-64"><SearchableSelect options={LABEL_OPSI} value={label} onChange={(v) => { setLabel(v || ""); setPage(1); }} placeholder="Semua label anggota" testId="filter-label-keluarga" /></div>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Memuat data...</div>
      ) : data.items.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl" data-testid="empty-keluarga">
          <Home className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Belum ada data keluarga</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="keluarga-grid">
          {data.items.map((f) => (
            <Card key={f.id} data-testid={`keluarga-card-${f.id}`} className="p-5 rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/keluarga/${f.id}`)}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-[hsl(224,64%,33%)]/10 flex items-center justify-center text-[hsl(224,64%,33%)]">
                    <Home className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{f.nama_keluarga}</div>
                    {f.nomor_kk && <div className="font-mono text-xs text-slate-500">KK: {f.nomor_kk}</div>}
                    {sealamat[f.id] > 0 && <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" title="Jemaat tanpa KK yang beralamat sama" data-testid={`sealamat-badge-${f.id}`}><MapPinned className="h-3 w-3" /> {sealamat[f.id]} jemaat sealamat tanpa KK</div>}
                  </div>
                </div>
                <span className="flex items-center gap-1 text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                  <Users className="h-3.5 w-3.5" /> {f.jumlah_anggota}
                </span>
              </div>
              {f.nama_kepala_keluarga && (
                <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                  <Crown className="h-4 w-4 text-amber-500" /> Kepala Keluarga: <span className="font-medium">{f.nama_kepala_keluarga}</span>
                </div>
              )}
              {labels[f.id]?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`label-keluarga-${f.id}`}>
                {labels[f.id].includes("non_kristen") && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5"><Church className="h-3 w-3" /> Ada anggota Non-Kristen</span>}
                {labels[f.id].includes("belum_baptis") && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5"><Droplets className="h-3 w-3" /> Ada anggota Belum Baptis</span>}
              </div>}
              {f.alamat && <p className="text-xs text-slate-400 mt-2 truncate">{f.alamat}</p>}
              {f.household_id && <p className="text-xs text-[hsl(224,64%,33%)] mt-1 flex items-center gap-1"><Building2 className="h-3 w-3" /> Tergabung dalam Satu Atap</p>}
            </Card>
          ))}
        </div>
      )}

      {data.total > 0 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="icon" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="kel-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-slate-600">Halaman {data.page} dari {data.total_pages}</span>
          <Button variant="outline" size="icon" className="rounded-xl" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} data-testid="kel-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
